import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Save, X, RefreshCw,
  Camera, Video, Edit, Users, CheckCircle, AlertCircle, Upload, Image
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFESSIONAL_ROLES = ["Photographer", "Videographer", "Photo Editor", "Video Editor"];

const AdminProfessionals = () => {
  const { toast } = useToast();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterRole, setFilterRole] = useState('');
  const [uploadingImage, setUploadingImage] = useState(null);

  const emptyForm = {
    name: '',
    role: 'Photographer',
    specialties: [],
    experience: '',
    description: '',
    portfolio: '',
    availability: 'Available for bookings'
  };
  
  const [formData, setFormData] = useState(emptyForm);
  const [specialtyInput, setSpecialtyInput] = useState('');

  useEffect(() => {
    fetchProfessionals();
  }, []);

  const fetchProfessionals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/professionals`);
      setProfessionals(response.data);
    } catch (error) {
      console.error('Error fetching professionals:', error);
      toast({
        title: "Error",
        description: "Failed to load professionals",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !formData.specialties.includes(specialtyInput.trim())) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, specialtyInput.trim()]
      }));
      setSpecialtyInput('');
    }
  };

  const removeSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Name is required",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // Update existing
        await axios.patch(`${API}/professionals/${editingId}`, formData);
        toast({
          title: "Success",
          description: "Professional updated successfully"
        });
      } else {
        // Create new
        await axios.post(`${API}/professionals`, formData);
        toast({
          title: "Success",
          description: "Professional created successfully"
        });
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData(emptyForm);
      fetchProfessionals();
    } catch (error) {
      console.error('Error saving professional:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save professional",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (professional) => {
    setFormData({
      name: professional.name || '',
      role: professional.role || 'Photographer',
      specialties: professional.specialties || [],
      experience: professional.experience || '',
      description: professional.description || '',
      portfolio: professional.portfolio || '',
      availability: professional.availability || 'Available for bookings'
    });
    setEditingId(professional.id);
    setShowForm(true);
  };

  const handleDelete = async (professional) => {
    if (!window.confirm(`Are you sure you want to deactivate ${professional.name}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/professionals/${professional.id}`);
      toast({
        title: "Success",
        description: "Professional deactivated successfully"
      });
      fetchProfessionals();
    } catch (error) {
      console.error('Error deleting professional:', error);
      toast({
        title: "Error",
        description: "Failed to deactivate professional",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyForm);
    setSpecialtyInput('');
  };

  const handleImageUpload = async (professionalId, file) => {
    setUploadingImage(professionalId);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      await axios.post(`${API}/professionals/${professionalId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast({
        title: "Success",
        description: "Profile image uploaded"
      });
      fetchProfessionals();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploadingImage(null);
    }
  };

  const handleImageDelete = async (professionalId) => {
    if (!window.confirm('Remove profile image?')) return;
    
    try {
      await axios.delete(`${API}/professionals/${professionalId}/image`);
      toast({
        title: "Success",
        description: "Profile image removed"
      });
      fetchProfessionals();
    } catch (error) {
      console.error('Error removing image:', error);
      toast({
        title: "Error",
        description: "Failed to remove image",
        variant: "destructive"
      });
    }
  };

  const getRoleIcon = (role) => {
    if (role === "Photographer") return Camera;
    if (role === "Videographer") return Video;
    return Edit;
  };

  const filteredProfessionals = filterRole 
    ? professionals.filter(p => p.role === filterRole)
    : professionals;

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link 
              to="/ews-admin-panel"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors mb-4"
            >
              <ArrowLeft size={18} /> Back to Admin
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-white">
              Manage <span className="text-red-500">Professionals</span>
            </h1>
            <p className="text-gray-400 mt-2">Add, edit, or remove professionals from the directory</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchProfessionals}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="refresh-professionals"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => { setShowForm(true); setEditingId(null); setFormData(emptyForm); }}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
              data-testid="add-professional-btn"
            >
              <Plus size={18} /> Add Professional
            </button>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-red-900/30">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">
                  {editingId ? 'Edit Professional' : 'Add New Professional'}
                </h2>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                    placeholder="Full name"
                    data-testid="professional-name-input"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Role *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                    data-testid="professional-role-select"
                  >
                    {PROFESSIONAL_ROLES.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                {/* Specialties */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Specialties</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={specialtyInput}
                      onChange={(e) => setSpecialtyInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                      className="flex-1 bg-black border border-red-900/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500"
                      placeholder="Add specialty and press Enter"
                    />
                    <button
                      type="button"
                      onClick={addSpecialty}
                      className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.specialties.map((specialty, idx) => (
                      <span 
                        key={idx}
                        className="bg-red-600/20 text-red-400 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      >
                        {specialty}
                        <button
                          type="button"
                          onClick={() => removeSpecialty(specialty)}
                          className="hover:text-white transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Experience */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Experience</label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                    placeholder="e.g., 10+ years"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 resize-none"
                    placeholder="Brief description of their work and expertise"
                  />
                </div>

                {/* Portfolio URL */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Portfolio URL</label>
                  <input
                    type="text"
                    name="portfolio"
                    value={formData.portfolio}
                    onChange={handleChange}
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                    placeholder="www.portfolio.com"
                  />
                </div>

                {/* Availability */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Availability</label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                  >
                    <option value="Available for bookings">Available for bookings</option>
                    <option value="Limited availability">Limited availability</option>
                    <option value="Currently unavailable">Currently unavailable</option>
                  </select>
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-3 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="save-professional-btn"
                  >
                    {saving ? 'Saving...' : (
                      <>
                        <Save size={18} /> {editingId ? 'Update' : 'Create'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setFilterRole('')}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${
              !filterRole ? 'bg-red-600 text-white' : 'bg-zinc-800 text-gray-400 hover:text-white'
            }`}
          >
            All ({professionals.length})
          </button>
          {PROFESSIONAL_ROLES.map(role => {
            const count = professionals.filter(p => p.role === role).length;
            return (
              <button
                key={role}
                onClick={() => setFilterRole(role)}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  filterRole === role ? 'bg-red-600 text-white' : 'bg-zinc-800 text-gray-400 hover:text-white'
                }`}
              >
                {role}s ({count})
              </button>
            );
          })}
        </div>

        {/* Professionals List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredProfessionals.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 rounded-lg border border-red-900/20">
            <Users className="mx-auto text-gray-600 mb-4" size={48} />
            <p className="text-gray-400">No professionals found</p>
            <button
              onClick={() => { setShowForm(true); setEditingId(null); setFormData(emptyForm); }}
              className="mt-4 text-red-400 hover:text-red-300 transition-colors"
            >
              Add your first professional
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProfessionals.map(professional => {
              const IconComponent = getRoleIcon(professional.role);
              return (
                <div 
                  key={professional.id}
                  className="bg-zinc-900 rounded-lg p-6 border border-red-900/20 hover:border-red-600/50 transition-all"
                  data-testid={`professional-card-${professional.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* Profile Image or Icon */}
                      <div className="relative group">
                        {professional.image_url ? (
                          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-red-600/30">
                            <img 
                              src={`${BACKEND_URL}${professional.image_url}`}
                              alt={professional.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-red-600/20 flex items-center justify-center">
                            <IconComponent className="text-red-500" size={24} />
                          </div>
                        )}
                        {/* Image upload overlay */}
                        <label className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                          {uploadingImage === professional.id ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Camera size={16} className="text-white" />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => e.target.files[0] && handleImageUpload(professional.id, e.target.files[0])}
                            disabled={uploadingImage === professional.id}
                          />
                        </label>
                        {/* Remove image button */}
                        {professional.image_url && (
                          <button
                            onClick={() => handleImageDelete(professional.id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove image"
                          >
                            <X size={12} className="text-white" />
                          </button>
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{professional.name}</h3>
                        <p className="text-red-400 text-sm">{professional.role}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(professional)}
                        className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                        title="Edit"
                        data-testid={`edit-professional-${professional.id}`}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(professional)}
                        className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                        title="Delete"
                        data-testid={`delete-professional-${professional.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {professional.description && (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {professional.description}
                    </p>
                  )}

                  {professional.specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {professional.specialties.slice(0, 3).map((specialty, idx) => (
                        <span 
                          key={idx}
                          className="bg-zinc-800 text-gray-300 px-2 py-1 rounded text-xs"
                        >
                          {specialty}
                        </span>
                      ))}
                      {professional.specialties.length > 3 && (
                        <span className="text-gray-500 text-xs px-2 py-1">
                          +{professional.specialties.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                    <span className="text-gray-500 text-sm">{professional.experience || 'N/A'}</span>
                    <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                      professional.availability === 'Available for bookings'
                        ? 'bg-green-600/20 text-green-400'
                        : professional.availability === 'Limited availability'
                        ? 'bg-yellow-600/20 text-yellow-400'
                        : 'bg-red-600/20 text-red-400'
                    }`}>
                      {professional.availability === 'Available for bookings' ? (
                        <CheckCircle size={12} />
                      ) : (
                        <AlertCircle size={12} />
                      )}
                      {professional.availability?.split(' ')[0]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats Summary */}
        <div className="mt-8 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
          <h3 className="text-lg font-semibold text-white mb-4">Directory Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-500">{professionals.length}</p>
              <p className="text-gray-400 text-sm">Total</p>
            </div>
            {PROFESSIONAL_ROLES.map(role => (
              <div key={role} className="text-center">
                <p className="text-3xl font-bold text-white">
                  {professionals.filter(p => p.role === role).length}
                </p>
                <p className="text-gray-400 text-sm">{role}s</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfessionals;
